import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const KEY = 'selectedStoreId';

// Active store (Boutique) the admin is working on, chosen in the admin menu.
export const getSelectedStoreId = () => {
  try {
    return localStorage.getItem(KEY) || null;
  } catch {
    return null;
  }
};

export const setSelectedStoreId = (id) => {
  try {
    if (id === null || id === undefined || id === '') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, String(id));
  } catch { /* ignore */ }
};

// TODO: switch to GET /Boutique/Mine once the backend links users to stores.
export const fetchStores = async () => {
  const response = await axios.get(`${API_URL}/Boutique`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
  });
  return Array.isArray(response.data) ? response.data : [];
};

// The admin always works on one store: keep the current selection while it still exists,
// otherwise select the first store. Returns the selected id (null when no store exists).
export const ensureSelectedStore = async () => {
  const stores = await fetchStores();
  const current = getSelectedStoreId();
  if (stores.some((s) => String(s.id) === String(current))) return current;
  const first = stores.length > 0 ? String(stores[0].id) : null;
  setSelectedStoreId(first);
  return first;
};

// Append boutiqueId to Dashboard GETs so stats follow the active store.
// (The Dashboard API rejects requests without it.)
let interceptorInstalled = false;
export const installStoreInterceptor = () => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.request.use((config) => {
    const id = getSelectedStoreId();
    const url = config.url || '';
    if (id && (config.method || 'get').toLowerCase() === 'get' && url.includes('/Dashboard/') && !/[?&]boutiqueId=/i.test(url)) {
      config.url = `${url}${url.includes('?') ? '&' : '?'}boutiqueId=${encodeURIComponent(id)}`;
    }
    return config;
  });
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// Public lookup: which store does the current domain belong to?
// Resolves to null when the domain isn't a registered store (e.g. plain localhost, admin domain).
let currentStorePromise = null;
export const resolveCurrentStore = () => {
  if (!currentStorePromise) {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    currentStorePromise = host
      ? axios
          .get(`${API_URL}/Boutique/ByDns`, { params: { host } })
          .then((r) => r.data || null)
          .catch(() => null)
      : Promise.resolve(null);
  }
  return currentStorePromise;
};

const toFormData = (store, { images = [], pathsToDelete = [], logo = null, deleteLogo = false } = {}) => {
  const fd = new FormData();
  if (store.id != null) fd.append('Id', store.id);
  fd.append('Nom', store.nom || '');
  fd.append('Dns', (store.dns || '').trim().toLowerCase());
  fd.append('CouleurPrincipale', store.couleurPrincipale || '');
  fd.append('CouleurSecondaire', store.couleurSecondaire || '');
  // Theme is no longer configurable; the column stays in the DB but is unused.
  fd.append('BannerText', store.bannerText || '');
  // The logo is its own column (Boutique.LogoUrl), not part of the Images gallery.
  if (logo) fd.append('Logo', logo);
  if (deleteLogo) fd.append('DeleteLogo', 'true');
  images.forEach((file) => fd.append('Images', file));
  pathsToDelete.forEach((p) => fd.append('PathForDelete', p));
  return fd;
};

export const createStore = async (store, { logo = null, images = [] } = {}) => {
  const r = await axios.post(`${API_URL}/Boutique`, toFormData(store, { logo, images }), { headers: authHeaders() });
  return r.data;
};

export const updateStore = async (store, { logo = null, deleteLogo = false, images = [], pathsToDelete = [] } = {}) => {
  const r = await axios.put(
    `${API_URL}/Boutique`,
    toFormData(store, { logo, deleteLogo, images, pathsToDelete }),
    { headers: authHeaders() },
  );
  return r.data;
};

// Resolves to false when the store still has products or orders.
export const deleteStore = async (id) => {
  const r = await axios.delete(`${API_URL}/Boutique/${id}`, { headers: authHeaders() });
  return r.data === true;
};

// Store to scope data to: the admin's selection on /admin pages,
// otherwise the store that owns the current domain (storefront).
export const getActiveStoreId = async () => {
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
    return getSelectedStoreId();
  }
  const store = await resolveCurrentStore();
  return store ? String(store.id) : null;
};
